<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Partners</h1>
            <div class="flex items-center space-x-2">
                <UInput 
                    v-model="search" 
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    Add Partner
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="partners"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-handshake-duotone', label: 'No partners.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #avatar-data="{ row }">
                    <div class="flex flex-col items-center">
                        <Avatar bucket="media" folder="partners" size="md" :placeholder="row.name" :path="row.avatar" />
                    </div>
                </template>
                <template #subscriptions-data="{ row }">
                    <p class="font-mono text-right">
                        {{ row.subscriptions?.length ? row.subscriptions[0].count : 0 }}
                    </p>
                </template>
                <template #coupons-data="{ row }">
                    <p class="font-mono text-right">
                        {{ row.coupons?.length ? row.coupons[0].count : 0 }}
                    </p>
                </template>
                <template #credentials-data="{ row }">
                    <p class="font-mono text-right">
                        {{ row.credentials?.length ? row.credentials[0].count : 0 }}
                    </p>
                </template>
                <template #visibility-data="{ row }">
                    <div class="flex justify-end">
                        <UBadge 
                            :label="row.metadata.is_public ? 'Public' : 'Private'" 
                            :color="row.metadata.is_public ? 'blue' : 'orange'" 
                            size="sm"
                            variant="subtle"
                        />
                    </div>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayPartnerCreatePartner v-model:open="overlays.create" @reload="fetchData" />
        <OverlayPartnerEditPartner v-model:open="overlays.edit" @reload="fetchData" :partner="selectedPartner" />
        <OverlayPartnerRemovePartner v-model:open="overlays.remove" @reload="fetchData" :partner="selectedPartner" />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { watchDebounced } from '@vueuse/core'
import type { Partner } from '~/types/partner'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Partners' })

const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const search = ref('')
const partners = ref<Partner[]>([])
const selectedPartner = ref<Partner>()

const overlays = reactive({
    create: false,
    edit: false,
    remove: false
})

const columns = [
    { key: 'avatar', class: 'w-px' },
    { key: 'name', label: 'Name', class: 'w-full' },
    { key: 'subscriptions', label: 'Subscribers' },
    { key: 'coupons', label: 'Coupons' },
    { key: 'credentials', label: 'Credentials' },
    { key: 'visibility', class: 'w-px' },
    { key: 'actions', class: 'w-px' },
]

const actions = (partner: Partner) => [
    [
        {
            label: 'Edit',
            icon: 'i-ph-pencil-fill',
            click: () => {
                selectedPartner.value = partner
                overlays.edit = true
            },
        },
        {
            label: 'Remove',
            icon: 'i-ph-trash-fill',
            iconClass: 'text-red-400',
            click: () => {
                selectedPartner.value = partner
                overlays.remove = true
            },
        },
    ],
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })
watch(() => pagination.from, () => fetchData())

async function fetchData() {
    loading.value = true
    partners.value = []
    pagination.setTotal(undefined)

    let query = client.from('partners')
        .select(`
            id, number, name, avatar, metadata, updated_at,
            subscriptions (count),
            coupons (count),
            credentials (count)
        `, { count: 'exact' })
        .is('deleted_at', null)
        .is('coupons.deleted_at', null)
        .is('credentials.invalidated_at', null)
        .order('name')
        .range(pagination.from, pagination.to)

    if (search.value) {
        query = query.ilike('name', `%${search.value}%`)
    }

    const { count, data } = await query

    if (count && data) {
        partners.value = data
        pagination.setTotal(count)
    }
    
    loading.value = false
}
</script>
